import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/dashboard/StatCard";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { CategoryRevenueGrid } from "@/components/dashboard/CategoryRevenueGrid";
import { MonthlyTrendCard } from "@/components/dashboard/MonthlyTrendCard";
import { TopSupportersCard } from "@/components/dashboard/TopSupportersCard";
import { RecentImportsCard } from "@/components/dashboard/RecentImportsCard";
import { Button } from "@/components/ui/button";
import { Users, HandCoins, ShoppingBag, GitMerge, ArrowRight, FileDown } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").split(" ")[0];
  const year = data?.year || new Date().getFullYear();

  const downloadSnapshot = async () => {
    setSnapshotBusy(true);
    try {
      const res = await api.get("/reports/dashboard-snapshot", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ministry-snapshot-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your board snapshot is downloading.");
    } catch (e) {
      toast.error("We couldn't create the snapshot. Please try again.");
    } finally {
      setSnapshotBusy(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold">{greeting}{firstName ? `, ${firstName}` : ""}.</h1>
          <p className="text-muted-foreground mt-2">Here is how the ministry is doing today.</p>
        </div>
        <Button variant="secondary" onClick={downloadSnapshot} disabled={snapshotBusy} data-testid="dashboard-snapshot-btn">
          <FileDown className="h-4 w-4 mr-2" /> {snapshotBusy ? "Preparing…" : "Board snapshot (PDF)"}
        </Button>
      </div>

      {data?.pending_duplicates > 0 && (
        <Link
          to="/duplicates"
          data-testid="duplicates-alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-primary/30 bg-secondary px-6 py-4 hover:bg-secondary/70 transition-colors duration-200"
        >
          <div className="flex items-center gap-3">
            <GitMerge className="h-5 w-5 text-primary" />
            <p className="text-sm">
              <span className="font-semibold">{data.pending_duplicates} possible duplicate{data.pending_duplicates > 1 ? "s" : ""}</span>{" "}
              waiting for your review.
            </p>
          </div>
          <ArrowRight className="h-4 w-4 text-primary" />
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Users} label="Total customers" value={data ? data.total_customers.toLocaleString() : "—"} testId="stat-total-customers" />
        <StatCard icon={HandCoins} label={`Revenue in ${year}`} value={data ? fmtMoney(data.ytd_revenue) : "—"} sub="After refunds and cancellations" testId="stat-ytd-revenue" />
        <StatCard icon={ShoppingBag} label={`Orders in ${year}`} value={data ? data.ytd_orders.toLocaleString() : "—"} testId="stat-ytd-orders" />
        <StatCard icon={GitMerge} label="Waiting for review" value={data ? data.pending_duplicates : "—"} sub="Possible duplicate customers" testId="stat-pending-duplicates" />
      </div>

      <CategoryRevenueGrid data={data} />
      <QuickActions role={user?.role} />

      <div className="grid lg:grid-cols-2 gap-8">
        <MonthlyTrendCard data={data} />
        <TopSupportersCard data={data} />
      </div>

      <RecentImportsCard data={data} />
    </div>
  );
}
